import React, { useState, useEffect } from 'react';
import logger from '../utils/logger';

const ProfileDebugPanel = ({ userData }) => {
  const [storageData, setStorageData] = useState({
    localStorage: {},
    sessionStorage: {}
  });
  const [expanded, setExpanded] = useState(false);

  // Fetch storage data on mount and when userData changes
  useEffect(() => {
    refreshStorageData();
  }, [userData]);

  // Get all relevant storage data
  const refreshStorageData = () => {
    try {
      // Get localStorage items
      const localStorageData = {
        user: JSON.parse(localStorage.getItem('user') || 'null'),
        onboardingResponses: JSON.parse(localStorage.getItem('onboardingResponses') || 'null'),
        lastProfileResponse: JSON.parse(localStorage.getItem('lastProfileResponse') || 'null'),
        appLogs: JSON.parse(localStorage.getItem('appLogs') || '[]').slice(-10) // Last 10 logs
      };

      // Get sessionStorage items
      const sessionStorageData = {
        profileDataFetched: JSON.parse(sessionStorage.getItem('profileDataFetched') || 'null')
      };

      setStorageData({
        localStorage: localStorageData,
        sessionStorage: sessionStorageData
      });

      logger.debug('Debug panel refreshed storage data');
    } catch (error) {
      logger.error('Error refreshing storage data:', error);
    }
  };

  // Clear all storage data
  const clearAllStorage = () => {
    try {
      // Clear specific items from localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('onboardingResponses');
      localStorage.removeItem('lastProfileResponse');
      
      // Clear specific items from sessionStorage
      sessionStorage.removeItem('profileDataFetched');
      
      logger.debug('Cleared all profile storage data');
      refreshStorageData();
      
      // Force page reload to trigger fresh data fetch
      if (window.confirm('Storage cleared. Reload page to fetch fresh data?')) {
        window.location.reload();
      }
    } catch (error) {
      logger.error('Error clearing storage:', error);
    }
  };

  // Clear only session storage
  const clearSessionStorage = () => {
    try {
      sessionStorage.removeItem('profileDataFetched');
      logger.debug('Cleared session storage data');
      refreshStorageData();
      
      // Force page reload to trigger fresh data fetch
      if (window.confirm('Session storage cleared. Reload page to fetch fresh data?')) {
        window.location.reload();
      }
    } catch (error) {
      logger.error('Error clearing session storage:', error);
    }
  };

  // View logs in console
  const viewLogsInConsole = () => {
    const logs = logger.getLogs();
    console.log('Application Logs:', logs);
    logger.debug('Displayed logs in console');
    alert('Logs have been output to the console. Open developer tools to view them.');
  };

  // Toggle expanded state
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Format data for display
  const formatValue = (value) => {
    if (value === undefined || value === null) return 'null';
    if (typeof value === 'string') return value || '""';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  return (
    <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 my-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-700 flex items-center">
          <span className="bg-red-500 w-3 h-3 rounded-full mr-2"></span>
          Profile Debug Panel
        </h3>
        <button 
          onClick={toggleExpanded}
          className="text-sm bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {expanded && (
        <div className="mt-4">
          <div className="grid grid-cols-1 gap-4">
            {/* Onboarding Data Section */}
            <div className="bg-white p-3 rounded shadow-sm">
              <h4 className="font-medium text-gray-800 mb-2">Onboarding Data Status</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Why I Play Sport:</div>
                <div className={userData?.whyIPlaySport ? 'text-green-600 font-medium' : 'text-red-600'}>
                  {userData?.whyIPlaySport ? 'Present' : 'Missing'}
                </div>
                
                <div>Journey:</div>
                <div className={userData?.journey ? 'text-green-600 font-medium' : 'text-red-600'}>
                  {userData?.journey ? 'Present' : 'Missing'}
                </div>
                
                <div>Goals:</div>
                <div className={userData?.goals ? 'text-green-600 font-medium' : 'text-red-600'}>
                  {userData?.goals ? 'Present' : 'Missing'}
                </div>
                
                <div>Expectations:</div>
                <div className={userData?.expectations ? 'text-green-600 font-medium' : 'text-red-600'}>
                  {userData?.expectations ? 'Present' : 'Missing'}
                </div>
                
                <div>Current Mental State:</div>
                <div className={userData?.currentMentalState ? 'text-green-600 font-medium' : 'text-red-600'}>
                  {userData?.currentMentalState ? 'Present' : 'Missing'}
                </div>
                
                <div>Desired Mental State:</div>
                <div className={userData?.desiredMentalState ? 'text-green-600 font-medium' : 'text-red-600'}>
                  {userData?.desiredMentalState ? 'Present' : 'Missing'}
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={clearAllStorage} 
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
              >
                Clear All Storage
              </button>
              <button 
                onClick={clearSessionStorage} 
                className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-sm"
              >
                Clear Session Cache
              </button>
              <button 
                onClick={refreshStorageData} 
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
              >
                Refresh Data
              </button>
              <button 
                onClick={viewLogsInConsole} 
                className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
              >
                View Logs
              </button>
            </div>
            
            {/* Storage Data Tabs */}
            <div className="mt-2">
              <details className="mb-2">
                <summary className="cursor-pointer font-medium bg-gray-200 p-2 rounded">
                  User Data Object
                </summary>
                <pre className="bg-gray-800 text-green-300 p-3 rounded mt-2 text-xs overflow-auto max-h-60">
                  {formatValue(userData)}
                </pre>
              </details>
              
              <details className="mb-2">
                <summary className="cursor-pointer font-medium bg-gray-200 p-2 rounded">
                  Local Storage
                </summary>
                <pre className="bg-gray-800 text-green-300 p-3 rounded mt-2 text-xs overflow-auto max-h-60">
                  {formatValue(storageData.localStorage)}
                </pre>
              </details>
              
              <details className="mb-2">
                <summary className="cursor-pointer font-medium bg-gray-200 p-2 rounded">
                  Session Storage
                </summary>
                <pre className="bg-gray-800 text-green-300 p-3 rounded mt-2 text-xs overflow-auto max-h-60">
                  {formatValue(storageData.sessionStorage)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDebugPanel;
